/**
 * JobCrawls SEO Meta Generator v2
 *
 * Generates unique, indexable <title>, <meta description>, and robots
 * directives for search / filter pages in EN, FI and SV.
 *
 * v2 changes:
 *   - Proper Finnish grammar: city inessive/adessive cases, field partitive
 *   - Swedish "i/på" preposition handling for cities
 *   - Guaranteed minimum description length (≥140 chars) via fallback facts
 *   - Richer fallback content when data points are sparse
 */

import type {
  SRPLanguage,
  FilterType,
  ActiveFilter,
  AggregatedJobData,
  SEOMeta,
} from '../../types/srp-seo-meta'
import { truncateSeoTitle, DEFAULT_SEO_TITLE_MAX_LENGTH } from './truncate-seo-title'
import { getFinnishRoleGenitive } from '../i18n/finnish-role-genitive'
import { getRegionTagLabel, hasRegionTagLabel, getRegionTagType } from '../facets/region-tag-labels'
import { getFinnishRegionLocative } from '../i18n/finnish-region-locative'
import { getRobotsDirective as getRobotsDirectiveForMeta } from './srp-index-policy'
import finlandRegionHierarchy from '../facets/data/finland-region-hierarchy.json'
import { getFinnishMunicipalityForms } from '../location/finnish-municipality-forms'

/**
 * "{role, genitive} {noun}" for Finnish role titles ("Hitsaajan työpaikat"). A role name with no
 * Finnish genitive (an English-titled role such as "Account Executive") is joined with the
 * Finnish compound hyphen instead ("Account Executive -työpaikat"), which is the orthographic form
 * and what job seekers actually type. `lowercase` applies only to real Finnish genitives; a foreign
 * title keeps its casing.
 */
export function finnishRolePhrase(role: string, noun: string, lowercase = false): string {
  const gen = getFinnishRoleGenitive(role)
  if (gen === role.trim()) return `${role.trim()} -${noun}`
  return `${lowercase ? gen.toLowerCase() : gen} ${noun}`
}

export type { SRPLanguage, FilterType, ActiveFilter, AggregatedJobData, SEOMeta }

/**
 * regionTags facet values are gazetteer place ids / region codes, not display text (see
 * facets-shared.ts). Resolve them to their localized label so SEO copy reads "Helsinki", not
 * "658225". Unknown ids are dropped — there's no readable label to render, and a bare place id in
 * a title is worse than treating the URL as filterless.
 */
function resolveRegionTagDisplay(filters: ActiveFilter[], lang: SRPLanguage): ActiveFilter[] {
  return filters.flatMap((f) => {
    if (f.type !== 'regionTags') return [f]
    if (!hasRegionTagLabel(f.rawValue)) return []
    return [{ ...f, value: getRegionTagLabel(f.rawValue, lang) }]
  })
}

/**
 * Same as {@link resolveRegionTagDisplay}, but for `fi`, a maakunta (admin1) regionTags value's
 * display text is its locative form ("Uusimaalla") instead of the nominative ("Uusimaa"): used
 * only for the on-page H1 (`generateResultsHeading`), never the `<title>` tag or sitemap/allowlist
 * copy, which read more naturally as a plain noun phrase ("Avoimet työpaikat Uusimaa").
 */
function resolveRegionTagDisplayForHeading(filters: ActiveFilter[], lang: SRPLanguage): ActiveFilter[] {
  const resolved = resolveRegionTagDisplay(filters, lang)
  if (lang !== 'fi') return resolved
  return resolved.map((f) => {
    if (f.type !== 'regionTags' || getRegionTagType(f.rawValue) !== 'admin1') return f
    const locative = getFinnishRegionLocative(f.rawValue)
    return locative ? { ...f, value: locative } : f
  })
}

/** Number of municipalities/towns in a maakunta (admin1) region's checked-in gazetteer hierarchy,
 *  or 0 for a non-region / unknown placeId. Backs the region description's "N kuntaa" fact. */
function regionMunicipalityCount(placeId: string): number {
  const towns = (finlandRegionHierarchy as Record<string, unknown[]>)[placeId]
  return Array.isArray(towns) ? towns.length : 0
}

/** "One sentence with the count" municipality fact for a maakunta region's meta description. */
function regionMunicipalityFact(placeId: string, lang: SRPLanguage): string | null {
  const n = regionMunicipalityCount(placeId)
  if (n <= 0) return null
  switch (lang) {
    case 'fi': return `Alueella työpaikkoja ${n} kunnassa`
    case 'sv': return `Regionen omfattar ${n} kommuner`
    case 'en': return `Covering ${n} municipalities`
  }
}

// ============================================================================
// CONSTANTS
// ============================================================================

// normalisedTitle (role hubs) leads so a role x city page reads "Hitsaajan työpaikat Tampere",
// matching how the query is phrased ("hitsaaja työpaikat tampere").
const FILTER_PRIORITY: FilterType[] = [
  'normalisedTitle', 'location', 'regionTags', 'company', 'field', 'remoteness', 'requiredLanguages',
  'jobType', 'normalisedRoleLevel', 'companyIndustry', 'specialisations',
  'educationLevel', 'benefits', 'skillsAndTechnologies', 'peopleManagementTier',
]

const MIN_DESCRIPTION_LENGTH = 140
const MAX_DESCRIPTION_LENGTH = 160

// ============================================================================
// FINNISH CITY GRAMMAR
// ============================================================================

/**
 * Finnish cities use either the INESSIVE case (-ssa/-ssä) or the
 * ADESSIVE case (-lla/-llä) for "in [city]". There is no reliable rule;
 * it must be learned per city.
 *
 * This table covers the cities in the sitemap. Add new cities as you expand.
 * The key is the canonical city name (before the underscore in your URL
 * pattern, e.g. "Helsinki" from "Helsinki_Finland").
 */
interface FinnishCityForm {
  inessive: string    // Helsingissä, Espoossa, Turussa, Tampereella
  elative: string     // Helsingistä, Espoosta, Turusta, Tampereelta
  short?: string      // display name override
}

const FINNISH_CITY_FORMS: Record<string, FinnishCityForm> = {
  'Helsinki':       { inessive: 'Helsingissä',       elative: 'Helsingistä' },
  'Espoo':          { inessive: 'Espoossa',          elative: 'Espoosta' },
  'Vantaa':         { inessive: 'Vantaalla',         elative: 'Vantaalta' },
  'Tampere':        { inessive: 'Tampereella',        elative: 'Tampereelta' },
  'Turku':          { inessive: 'Turussa',           elative: 'Turusta' },
  'Oulu':           { inessive: 'Oulussa',           elative: 'Oulusta' },
  'Lahti':          { inessive: 'Lahdessa',          elative: 'Lahdesta' },
  'Kuopio':         { inessive: 'Kuopiossa',         elative: 'Kuopiosta' },
  'Jyväskylä':      { inessive: 'Jyväskylässä',      elative: 'Jyväskylästä' },
  'Pori':           { inessive: 'Porissa',           elative: 'Porista' },
  'Lappeenranta':   { inessive: 'Lappeenrannassa',   elative: 'Lappeenrannasta' },
  'Kotka':          { inessive: 'Kotkassa',          elative: 'Kotkasta' },
  'Joensuu':        { inessive: 'Joensuussa',        elative: 'Joensuusta' },
  'Rovaniemi':      { inessive: 'Rovaniemellä',      elative: 'Rovaniemeltä' },
  'Seinäjoki':      { inessive: 'Seinäjoella',       elative: 'Seinäjoelta' },
  'Vaasa':          { inessive: 'Vaasassa',          elative: 'Vaasasta' },
  'Porvoo':         { inessive: 'Porvoossa',         elative: 'Porvoosta' },
  'Hämeenlinna':    { inessive: 'Hämeenlinnassa',    elative: 'Hämeenlinnasta' },
  'Mikkeli':        { inessive: 'Mikkelissä',        elative: 'Mikkelistä' },
  'Kokkola':        { inessive: 'Kokkolassa',        elative: 'Kokkolasta' },
  'Kajaani':        { inessive: 'Kajaanissa',        elative: 'Kajaanista' },
  'Rauma':          { inessive: 'Raumalla',          elative: 'Raumalta' },
  'Lohja':          { inessive: 'Lohjalla',          elative: 'Lohjalta' },
  'Nurmijärvi':     { inessive: 'Nurmijärvellä',     elative: 'Nurmijärveltä' },
  'Hyvinkää':       { inessive: 'Hyvinkäällä',       elative: 'Hyvinkäältä' },
  'Kemi':           { inessive: 'Kemissä',           elative: 'Kemistä' },
  'Pietarsaari':    { inessive: 'Pietarsaaressa',    elative: 'Pietarsaaresta' },
  'Valkeakoski':    { inessive: 'Valkeakoskella',    elative: 'Valkeakoskelta' },
  'Kaskinen':       { inessive: 'Kaskisissa',        elative: 'Kaskisista' },
  // Long-tail towns that already earn "avoimet työpaikat {town}" impressions (GSC, Sep 2026)
  // and/or carry 100+ live jobs. Anything not listed here falls back to the case-free
  // "{Town}: selaa N avointa työpaikkaa" opener rather than a guessed (and often wrong) case.
  'Kirkkonummi':    { inessive: 'Kirkkonummella',    elative: 'Kirkkonummelta' },
  'Tuusula':        { inessive: 'Tuusulassa',        elative: 'Tuusulasta' },
  'Järvenpää':      { inessive: 'Järvenpäässä',      elative: 'Järvenpäästä' },
  'Kerava':         { inessive: 'Keravalla',         elative: 'Keravalta' },
  'Sipoo':          { inessive: 'Sipoossa',          elative: 'Sipoosta' },
  'Naantali':       { inessive: 'Naantalissa',       elative: 'Naantalista' },
  'Laitila':        { inessive: 'Laitilassa',        elative: 'Laitilasta' },
  'Lieto':          { inessive: 'Liedossa',          elative: 'Liedosta' },
  'Aura':           { inessive: 'Aurassa',           elative: 'Aurasta' },
  'Salla':          { inessive: 'Sallassa',          elative: 'Sallasta' },
  'Kangasala':      { inessive: 'Kangasalla',        elative: 'Kangasalta' },
  'Lempäälä':       { inessive: 'Lempäälässä',       elative: 'Lempäälästä' },
  'Pirkkala':       { inessive: 'Pirkkalassa',       elative: 'Pirkkalasta' },
  'Ylöjärvi':       { inessive: 'Ylöjärvellä',       elative: 'Ylöjärveltä' },
  'Kempele':        { inessive: 'Kempeleessä',       elative: 'Kempeleestä' },
  'Ii':             { inessive: 'Iissä',             elative: 'Iistä' },
  'Utajärvi':       { inessive: 'Utajärvellä',       elative: 'Utajärveltä' },
  'Riihimäki':      { inessive: 'Riihimäellä',       elative: 'Riihimäeltä' },
  'Orimattila':     { inessive: 'Orimattilassa',     elative: 'Orimattilasta' },
  'Iitti':          { inessive: 'Iitissä',           elative: 'Iitistä' },
  'Nivala':         { inessive: 'Nivalassa',         elative: 'Nivalasta' },
  'Haapajärvi':     { inessive: 'Haapajärvellä',     elative: 'Haapajärveltä' },
  'Haapavesi':      { inessive: 'Haapavedellä',      elative: 'Haapavedeltä' },
  'Merijärvi':      { inessive: 'Merijärvellä',      elative: 'Merijärveltä' },
  'Perho':          { inessive: 'Perhossa',          elative: 'Perhosta' },
  'Veteli':         { inessive: 'Vetelissä',         elative: 'Vetelistä' },
  'Teuva':          { inessive: 'Teuvalla',          elative: 'Teuvalta' },
  'Huittinen':      { inessive: 'Huittisissa',       elative: 'Huittisista' },
  'Raasepori':      { inessive: 'Raaseporissa',      elative: 'Raaseporista' },
}

/**
 * Inessive ("in {city}") when we have a vetted form for the city, otherwise null. Callers that
 * build flowing copy should switch to a case-free construction on null instead of the old
 * "sijainnissa {City}" fallback, which read as bureaucratic filler in SERP snippets.
 */
export function getCityInessiveOrNull(cityName: string): string | null {
  const city = cityName.split(/[,_]/)[0].trim()
  // The hand-curated table first, then every municipality from Kotus (finnish-municipality-forms.ts).
  return FINNISH_CITY_FORMS[city]?.inessive ?? getFinnishMunicipalityForms(city)?.locative ?? null
}

/**
 * Get the Finnish locative form of a city name ("in Helsinki" → "Helsingissä").
 *
 * Falls back to the safe prepositional form "sijainnissa [City]" for unknown
 * cities, which is always grammatically correct (if slightly formal).
 */
export function getCityInessive(cityName: string): string {
  const city = cityName.split(/[,_]/)[0].trim()
  const form = FINNISH_CITY_FORMS[city]
  if (form) return form.inessive
  const municipality = getFinnishMunicipalityForms(city)
  if (municipality) return municipality.locative

  if (typeof console !== 'undefined') {
    console.warn(
      `[SEO] Missing Finnish locative for city: "${city}" — ` +
      `add it to FINNISH_CITY_FORMS. Using safe fallback "sijainnissa ${city}".`
    )
  }
  return `sijainnissa ${city}`
}

/**
 * Flat, uninflected city name for keyword-style titles ("Avoimet työpaikat Helsinki").
 * Finnish job boards (Duunitori et al.) drop case inflection in titles even though
 * flowing text prefers it — use getCityInessive for body copy, this for titles.
 */
export function getFinnishCityName(cityName: string): string {
  const city = cityName.split(/[,_]/)[0].trim()
  return FINNISH_CITY_FORMS[city]?.short || city
}

/**
 * Finnish genitive forms for the `field` and `companyIndustry` (listingCompanyIndustry) facet
 * value sets — the nouns whose "of X" form leads "…n työpaikat". Both facets share this helper
 * and both draw from `locale-enum-fields.ts` (FIELD_FI / LISTING_COMPANY_INDUSTRY_FI), so the
 * table is keyed on the actual production values. Finnish genitive is irregular for -s and
 * loanword stems, so a naive "-alan" fallback was producing junk ("Ohjelmistokehitys-alan",
 * "Vähittäiskauppa-alan") — each value is spelled out here instead.
 */
const FINNISH_FIELD_GENITIVE: Record<string, string> = {
  // field facet (FIELD_FI)
  'Ohjelmistokehitys': 'Ohjelmistokehityksen',
  'Data ja analytiikka': 'Datan ja analytiikan',
  'Terveysala': 'Terveysalan',
  'Koulutusala': 'Koulutusalan',
  'Lakiala': 'Lakialan',
  'Talous ja kirjanpito': 'Talouden ja kirjanpidon',
  'Myynti': 'Myynnin',
  'Markkinointi': 'Markkinoinnin',
  'Henkilöstö': 'Henkilöstön',
  'Asiakaspalvelu': 'Asiakaspalvelun',
  'Design': 'Designin',
  'Tuote': 'Tuotteen',
  'Logistiikka ja osto': 'Logistiikan ja oston',
  'Rakennus- ja asennusala': 'Rakennus- ja asennusalan',
  'Ravintola- ja majoitusala': 'Ravintola- ja majoitusalan',
  'Siivous- ja kiinteistöpalvelut': 'Siivous- ja kiinteistöpalveluiden',
  'Vähittäiskauppa': 'Vähittäiskaupan',
  'Teollisuus ja tuotanto': 'Teollisuuden ja tuotannon',
  'Vartiointi ja turvallisuus': 'Vartioinnin ja turvallisuuden',
  'Teknologia': 'Teknologian',
  'Muu ala': 'Muun alan',
  // companyIndustry facet (LISTING_COMPANY_INDUSTRY_FI)
  'Finanssiala': 'Finanssialan',
  'Peliala': 'Pelialan',
  'Verkkokauppa': 'Verkkokaupan',
  'Konsultointi': 'Konsultoinnin',
  'Kiinteistöala': 'Kiinteistöalan',
  'Autoteollisuus': 'Autoteollisuuden',
  'Viihdeala': 'Viihdealan',
  'Terveydenhuolto': 'Terveydenhuollon',
  'Koulutus': 'Koulutuksen',
  'Julkishallinto': 'Julkishallinnon',
  'Valmistava teollisuus': 'Valmistavan teollisuuden',
  'Rakennusala': 'Rakennusalan',
  'Matkailu- ja majoitusala': 'Matkailu- ja majoitusalan',
  'Kuljetus ja logistiikka': 'Kuljetuksen ja logistiikan',
  'Maa- ja metsätalous': 'Maa- ja metsätalouden',
  'Kolmas sektori': 'Kolmannen sektorin',
  'Energia-ala': 'Energia-alan',
  'Asiantuntijapalvelut': 'Asiantuntijapalveluiden',
  'Televiestintä': 'Televiestinnän',
  'Media ja julkaisuala': 'Median ja julkaisualan',
}

export function getFieldGenitive(fieldName: string): string {
  if (FINNISH_FIELD_GENITIVE[fieldName]) return FINNISH_FIELD_GENITIVE[fieldName]
  const trimmed = fieldName.trim()
  // Values already ending in "-ala" take a plain genitive "-n" ("Rakennusala" → "Rakennusalan").
  if (/ala$/i.test(trimmed)) return `${trimmed}n`
  // Last-resort for a value not yet in the table: keep the bare nominative rather than guessing
  // a (usually wrong) genitive. It reads as a keyword-style modifier and is never actively
  // ungrammatical the way the old "-alan" glue was ("Ohjelmistokehitys-alan").
  return trimmed
}

// ============================================================================
// SWEDISH CITY GRAMMAR
// ============================================================================

interface SwedishCityForm {
  prep: 'i' | 'på'
  name?: string
}

const SWEDISH_CITY_FORMS: Record<string, SwedishCityForm> = {
  'Helsinki':     { prep: 'i', name: 'Helsingfors' },
  'Turku':        { prep: 'i', name: 'Åbo' },
  'Tampere':      { prep: 'i', name: 'Tammerfors' },
  'Vaasa':        { prep: 'i', name: 'Vasa' },
  'Porvoo':       { prep: 'i', name: 'Borgå' },
  'Pietarsaari':  { prep: 'i', name: 'Jakobstad' },
  'Espoo':        { prep: 'i', name: 'Esbo' },
  'Vantaa':       { prep: 'i', name: 'Vanda' },
  'Oulu':         { prep: 'i', name: 'Uleåborg' },
  'Kotka':        { prep: 'i' },
  'Kuopio':       { prep: 'i' },
  'Jyväskylä':    { prep: 'i' },
  'Lahti':        { prep: 'i', name: 'Lahtis' },
  'Hämeenlinna':  { prep: 'i', name: 'Tavastehus' },
  'Lappeenranta': { prep: 'i', name: 'Villmanstrand' },
  'Joensuu':      { prep: 'i' },
  'Rovaniemi':    { prep: 'i' },
  'Kokkola':      { prep: 'i', name: 'Karleby' },
  'Mikkeli':      { prep: 'i', name: 'S:t Michel' },
  'Kajaani':      { prep: 'i', name: 'Kajana' },
  'Pori':         { prep: 'i', name: 'Björneborg' },
  'Seinäjoki':    { prep: 'i' },
  'Rauma':        { prep: 'i', name: 'Raumo' },
  'Lohja':        { prep: 'i', name: 'Lojo' },
  'Hyvinkää':     { prep: 'i', name: 'Hyvinge' },
  'Nurmijärvi':   { prep: 'i' },
}

export function getSwedishCityPrep(cityName: string): string {
  const city = cityName.split(/[,_]/)[0].trim()
  const form = SWEDISH_CITY_FORMS[city]
  const name = form?.name || city
  const prep = form?.prep || 'i'
  return `${prep} ${name}`
}

export function getSwedishCityName(cityName: string): string {
  const city = cityName.split(/[,_]/)[0].trim()
  const form = SWEDISH_CITY_FORMS[city]
  return form?.name || city
}

// ============================================================================
// FINNISH LANGUAGE-REQUIREMENT FORMS
// ============================================================================

const FINNISH_LANGUAGE_ADJECTIVE: Record<string, string> = {
  'englanti': 'englanninkieliset',
  'suomi': 'suomenkieliset',
  'ruotsi': 'ruotsinkieliset',
  'saksa': 'saksankieliset',
  'ranska': 'ranskankieliset',
  'espanja': 'espanjankieliset',
  'venäjä': 'venäjänkieliset',
}

export function getFinnishLanguageAdjective(langValue: string): string {
  const lower = langValue.toLowerCase()
  return FINNISH_LANGUAGE_ADJECTIVE[lower] || `${langValue}kieliset`
}

const SWEDISH_LANGUAGE_ADJECTIVE: Record<string, string> = {
  'english': 'engelskspråkiga',
  'finnish': 'finskspråkiga',
  'swedish': 'svenskspråkiga',
  'german': 'tyskspråkiga',
  'engelska': 'engelskspråkiga',
  'finska': 'finskspråkiga',
  'svenska': 'svenskspråkiga',
}

export function getSwedishLanguageAdjective(langValue: string): string {
  const lower = langValue.toLowerCase()
  return SWEDISH_LANGUAGE_ADJECTIVE[lower] || `${langValue}språkiga`
}

// ============================================================================
// JOB TYPE FORMS
// ============================================================================

/**
 * Finnish jobType facet values are pre-translated singular adjectives/nouns
 * (see JOB_TYPE_TRANSLATIONS) — naively appending "työpaikat" produces either a
 * number-agreement mismatch ("Kokoaikainen työpaikat", adjective stays singular
 * while the noun is plural) or a duplicated job-noun ("Kesätyö työpaikat", since
 * "Kesätyö" already means "summer job"). This maps each value to the correct
 * base phrase (lowercase, embeddable — capitalize/prefix at the call site).
 */
const FINNISH_JOB_TYPE_TITLE: Record<string, string> = {
  'Kokoaikainen': 'kokoaikaiset työpaikat',
  'Osa-aikainen': 'osa-aikaiset työpaikat',
  'Harjoittelupaikka': 'harjoittelupaikat',
  'Määräaikainen': 'määräaikaiset työpaikat',
  'Kesätyö': 'kesätyöpaikat',
  'Avoin hakemus': 'hakemukset',
  'Sopimuspohjainen työ': 'sopimuspohjaiset työpaikat',
  'Keikkatyö': 'keikkatyöpaikat',
  'Oppisopimus': 'oppisopimuspaikat',
}

export function getFinnishJobTypeTitle(value: string): string {
  return FINNISH_JOB_TYPE_TITLE[value] || `${value.toLowerCase()} työpaikat`
}

/** Same problem in Swedish: "Sommarjobb" already ends in "jobb". */
const SWEDISH_JOB_TYPE_TITLE: Record<string, string> = {
  'Heltid': 'heltidsjobb',
  'Deltid': 'deltidsjobb',
  'Praktik': 'praktikplatser',
  'Tillfällig': 'tillfälliga jobb',
  'Sommarjobb': 'sommarjobb',
  'Öppen ansökan': 'öppna ansökningar',
  'Uppdrag': 'uppdragsjobb',
  'Frilans': 'frilansjobb',
  'Läroavtal': 'läroavtalsplatser',
}

export function getSwedishJobTypeTitle(value: string): string {
  return SWEDISH_JOB_TYPE_TITLE[value] || `${value.toLowerCase()}-jobb`
}

/** English hits the same issue: "Summer Job" + "Jobs" → "Summer Job Jobs". */
export function getEnglishJobTypeLabel(value: string): string {
  return /\bjob$/i.test(value) ? value.replace(/job$/i, 'Jobs') : `${value} Jobs`
}

/** Lowercase, mid-sentence form for description openers ("browse N summer jobs in..."). */
export function getEnglishJobTypeLabelLower(value: string): string {
  return /\bjob$/i.test(value)
    ? value.toLowerCase().replace(/job$/i, 'jobs')
    : `${value.toLowerCase()} jobs`
}

// ============================================================================
// REMOTENESS FORMS
// ============================================================================

/**
 * Two-filter titles that pair location/field with remoteness used to always
 * say "Remote"/"Etätyömahdollisuudet"/"Distansjobb" regardless of the actual value —
 * a Hybrid- or Onsite-filtered page was mislabeled as remote. These check the
 * real value instead.
 */
export function englishRemotenessLabel(filter: ActiveFilter): string {
  return filter.rawValue.toLowerCase() === 'remote' ? 'Remote' : capitalize(filter.value)
}

/**
 * Finnish "Hybridityö" and "Paikkasidonnainen työ" already end in "työ"
 * ("work") — naively appending "työpaikat" duplicates it, and "Paikkasidonnainen"
 * is a singular adjective that needs the plural "-set" ending to agree with the
 * plural noun ("Paikkasidonnaiset työpaikat", not "Paikkasidonnainen työpaikat").
 * Explicit map for the known values; regex fallback for anything unmapped.
 */
const FINNISH_REMOTENESS_JOB_NOUN: Record<string, string> = {
  'Hybridityö': 'hybridityöpaikat',
  'Paikkasidonnainen työ': 'paikkasidonnaiset työpaikat',
}

export function getFinnishRemotenessJobNoun(value: string): string {
  return (
    FINNISH_REMOTENESS_JOB_NOUN[value] ||
    (/työ$/i.test(value) ? value.replace(/työ$/i, 'työpaikat') : `${value} työpaikat`)
  )
}

/**
 * Swedish "På plats" (on-site) is a prepositional phrase, not a compoundable
 * word — naively fusing it with "jobb" produces "på platsjobb". This maps
 * each value to its correct lowercase, embeddable job-noun form.
 */
const SWEDISH_REMOTENESS_JOB_NOUN: Record<string, string> = {
  'Distans': 'distansjobb',
  'Hybrid': 'hybridjobb',
  'På plats': 'jobb på plats',
}

export function getSwedishRemotenessJobNoun(value: string): string {
  return SWEDISH_REMOTENESS_JOB_NOUN[value] || `${value.toLowerCase()}jobb`
}

// ============================================================================
// ROLE LEVEL FORMS
// ============================================================================

/**
 * Some Finnish role-level values already end in "taso" ("level") — e.g.
 * "Senior-taso", "Keskitaso" — so naively appending "-tason" duplicates it
 * ("Senior-taso-tason"). Add just the genitive "-n" when that's already the
 * case; otherwise append the full "-tason" suffix.
 */
export function getFinnishRoleLevelTitle(value: string): string {
  return /taso$/i.test(value) ? `${value}n` : `${value}-tason`
}

/** English mirror: "Mid-Level" + "-Level" → "Mid-Level-Level". */
export function getEnglishRoleLevelLabel(value: string): string {
  return /level$/i.test(value) ? value : `${value}-Level`
}

// ============================================================================
// EDUCATION LEVEL FORMS
// ============================================================================

/**
 * educationLevel facet values are the processor's search-friendly enum phrases (see
 * locale-enum-fields.ts). EN values like "Bachelor"/"Masters" read wrong glued to "Jobs"
 * ("Bachelor Jobs"), so map them to the noun-phrase form people actually search
 * ("Bachelor's Degree Jobs", "No Degree Required Jobs"). Unknown values pass through unchanged.
 */
const ENGLISH_EDUCATION_LEVEL_TITLE: Record<string, string> = {
  'No degree required': 'No Degree Required',
  'High school': 'High School',
  'Bachelor': "Bachelor's Degree",
  'Masters': "Master's Degree",
  'Doctorate': 'Doctorate',
  'Apprenticeship': 'Apprenticeship',
  'Diploma': 'Diploma',
}

export function getEnglishEducationLevelLabel(value: string): string {
  return ENGLISH_EDUCATION_LEVEL_TITLE[value] || value
}

/**
 * Finnish educationLevel values are full requirement phrases ("Ei koulutusta vaadita",
 * "Alempi korkeakoulututkinto"), which can't be glued to "työpaikat" as-is without breaking
 * agreement/word order. Map each to its natural noun-phrase form, lowercase and embeddable
 * (capitalize at the call site).
 */
const FINNISH_EDUCATION_LEVEL_TITLE: Record<string, string> = {
  'Ei koulutusta vaadita': 'työpaikat ilman koulutusvaatimusta',
  'Toisen asteen koulutus': 'toisen asteen koulutuksen työpaikat',
  'Alempi korkeakoulututkinto': 'alemman korkeakoulututkinnon työpaikat',
  'Ylempi korkeakoulututkinto': 'ylemmän korkeakoulututkinnon työpaikat',
  'Tieteellinen jatkotutkinto': 'tieteellisen jatkotutkinnon työpaikat',
  'Oppisopimuskoulutus': 'oppisopimuskoulutuksen työpaikat',
  'Diplomi': 'diplomityöpaikat',
}

export function getFinnishEducationLevelTitle(value: string): string {
  return FINNISH_EDUCATION_LEVEL_TITLE[value] || `${value.toLowerCase()} työpaikat`
}

/** Count/plural partitive forms for the same Finnish values, for the flowing "Selaa N …" opener. */
const FINNISH_EDUCATION_LEVEL_DESCRIPTION: Record<string, { singular: string; plural: string }> = {
  'Ei koulutusta vaadita': { singular: 'työpaikkaa ilman koulutusvaatimusta', plural: 'työpaikkoja ilman koulutusvaatimusta' },
  'Toisen asteen koulutus': { singular: 'toisen asteen koulutuksen työpaikkaa', plural: 'toisen asteen koulutuksen työpaikkoja' },
  'Alempi korkeakoulututkinto': { singular: 'alemman korkeakoulututkinnon työpaikkaa', plural: 'alemman korkeakoulututkinnon työpaikkoja' },
  'Ylempi korkeakoulututkinto': { singular: 'ylemmän korkeakoulututkinnon työpaikkaa', plural: 'ylemmän korkeakoulututkinnon työpaikkoja' },
  'Tieteellinen jatkotutkinto': { singular: 'tieteellisen jatkotutkinnon työpaikkaa', plural: 'tieteellisen jatkotutkinnon työpaikkoja' },
  'Oppisopimuskoulutus': { singular: 'oppisopimuskoulutuksen työpaikkaa', plural: 'oppisopimuskoulutuksen työpaikkoja' },
  'Diplomi': { singular: 'diplomityöpaikkaa', plural: 'diplomityöpaikkoja' },
}

function getFinnishEducationLevelDescription(value: string): { singular: string; plural: string } {
  return FINNISH_EDUCATION_LEVEL_DESCRIPTION[value] || {
    singular: `${value.toLowerCase()} työpaikkaa`,
    plural: `${value.toLowerCase()} työpaikkoja`,
  }
}

/** Swedish educationLevel values → compound/prefix noun form ("ingen utbildning krävs" → "jobb utan utbildningskrav"). */
const SWEDISH_EDUCATION_LEVEL_TITLE: Record<string, string> = {
  'Ingen utbildning krävs': 'jobb utan utbildningskrav',
  'Gymnasieutbildning': 'gymnasiejobb',
  'Kandidat': 'kandidatjobb',
  'Magister': 'magisterjobb',
  'Doktorsexamen': 'doktorsjobb',
  'Läroavtalsutbildning': 'lärlingsjobb',
  'Diplom': 'diplomjobb',
}

export function getSwedishEducationLevelTitle(value: string): string {
  return SWEDISH_EDUCATION_LEVEL_TITLE[value] || `${value.toLowerCase()}-jobb`
}

/**
 * Finnish jobType values are already pre-translated (see FINNISH_JOB_TYPE_TITLE), but that map
 * returns nominative plural forms for titles ("kesätyöpaikat"). The description opener needs
 * partitive forms that agree with "Selaa N …" — map each value to its count/plural pair.
 */
const FINNISH_JOB_TYPE_DESCRIPTION: Record<string, { singular: string; plural: string }> = {
  'Kokoaikainen': { singular: 'kokoaikaista työpaikkaa', plural: 'kokoaikaisia työpaikkoja' },
  'Osa-aikainen': { singular: 'osa-aikaista työpaikkaa', plural: 'osa-aikaisia työpaikkoja' },
  'Harjoittelupaikka': { singular: 'harjoittelupaikkaa', plural: 'harjoittelupaikkoja' },
  'Määräaikainen': { singular: 'määräaikaista työpaikkaa', plural: 'määräaikaisia työpaikkoja' },
  'Kesätyö': { singular: 'kesätyöpaikkaa', plural: 'kesätyöpaikkoja' },
  'Avoin hakemus': { singular: 'avointa hakemusta', plural: 'avoimia hakemuksia' },
  'Sopimuspohjainen työ': { singular: 'sopimuspohjaista työpaikkaa', plural: 'sopimuspohjaisia työpaikkoja' },
  'Keikkatyö': { singular: 'keikkatyöpaikkaa', plural: 'keikkatyöpaikkoja' },
  'Oppisopimus': { singular: 'oppisopimuspaikkaa', plural: 'oppisopimuspaikkoja' },
}

function getFinnishJobTypeDescription(value: string): { singular: string; plural: string } {
  return FINNISH_JOB_TYPE_DESCRIPTION[value] || {
    singular: `${value.toLowerCase()} työpaikkaa`,
    plural: `${value.toLowerCase()} työpaikkoja`,
  }
}

// ============================================================================
// MAIN ENTRY POINT
// ============================================================================

export function generateSEOMeta(
  activeFilters: ActiveFilter[],
  jobData: AggregatedJobData,
  lang: SRPLanguage,
  pageUrl: string,
): SEOMeta {
  const filters = resolveRegionTagDisplay(activeFilters, lang)
  const robots = getRobotsDirectiveForMeta(filters, jobData.count)
  const title = generateTitle(filters, jobData.count, lang)
  const description = generateDescription(filters, jobData, lang)

  return { title, description, robots, canonical: pageUrl }
}

// ============================================================================
// ROBOTS DIRECTIVE
// ============================================================================

// The filter-combination policy lives in ./srp-index-policy (TEC-697) so every surface that
// has to agree on it (robots meta + X-Robots-Tag, canonical, link rel, sitemaps, backoffice) reads
// one module. Re-exported here for existing importers.
export { getRobotsDirective, explainRobotsDirective, MIN_JOBS_FOR_INDEX } from './srp-index-policy'
export type { RobotsDirectiveExplanation } from './srp-index-policy'

// ============================================================================
// TITLE GENERATION
// ============================================================================

/**
 * Builds the title phrase with its internal count clause separated by an en dash
 * (" – {count} ..."). This is the shared core: `generateTitle` (the `<title>` tag) swaps that
 * dash for a pipe before returning, but `generateResultsHeading` (the on-page H1) uses this
 * dash-separated form directly, so the tab title and the H1 don't read identically.
 */
function buildTitleCore(filters: ActiveFilter[], count: number | null, lang: SRPLanguage): string {
  const sorted = sortByPriority(filters)
  const p = sorted[0] || null
  const s = sorted[1] || null
  const c = count != null && count > 0 ? fmtCount(count, lang) : null

  if (!p) return homepageTitle(c, lang)
  if (!s) return singleFilterTitle(p, c, lang)
  return twoFilterTitle(p, s, c, lang)
}

export function generateTitle(filters: ActiveFilter[], count: number | null, lang: SRPLanguage): string {
  const sorted = sortByPriority(resolveRegionTagDisplay(filters, lang))
  if (sorted.length >= 3) {
    const c = count != null && count > 0 ? fmtCount(count, lang) : null
    return multiFilterTitle(sorted, c, lang).replace(/ – /g, ' | ')
  }
  return buildTitleCore(sorted, count, lang).replace(/ – /g, ' | ')
}

/**
 * Per-filter-type text for the tag-list suffix. Most filter values (job type, remoteness, role
 * level, company, field, etc.) already read fine as bare tags — they're the same raw facet
 * labels TopFilters already shows as pills. requiredLanguages is the exception: the raw facet
 * value is a bare language noun ("Englanti"/"English"), which reads as a stray word rather than
 * "requires this language" as plain text after "·" with no pill/icon for context — so it's
 * turned into the existing language-adjective form (already used by the title generators above)
 * instead of the raw noun.
 */
function filterTagFragment(filter: ActiveFilter, lang: SRPLanguage): string {
  if (filter.type === 'regionTags') {
    return hasRegionTagLabel(filter.rawValue) ? getRegionTagLabel(filter.rawValue, lang) : filter.value
  }
  if (filter.type === 'requiredLanguages') {
    switch (lang) {
      case 'fi': return capitalize(getFinnishLanguageAdjective(filter.value))
      case 'sv': return capitalize(getSwedishLanguageAdjective(filter.value))
      case 'en': return `${capitalize(filter.value)}-Speaking`
    }
  }
  return filter.value
}

/**
 * Plain "· tag, tag" list for filters not otherwise reflected in a headline. Returns '' when
 * there's nothing to add, so callers can always append the result directly.
 */
export function buildFilterTagSuffix(filters: ActiveFilter[], lang: SRPLanguage): string {
  if (filters.length === 0) return ''
  return ` · ${sortByPriority(filters).map(f => filterTagFragment(f, lang)).join(', ')}`
}

/**
 * On-page <h1> text — deliberately longer and more specific than `generateTitle`'s `<title>`
 * tag, which is capped short to avoid SERP truncation. Reuses the same already-correct en/fi/sv
 * grammar and count formatting for the top 2 filters (via `buildTitleCore`, which keeps its en
 * dash count-clause separator here rather than `generateTitle`'s pipe, so the H1 doesn't read
 * identically to the tab title), then folds in every additional active filter as a plain tag
 * list (`buildFilterTagSuffix`) so the H1 reflects the full applied filter set, not just 2 of them.
 */
const COUNTLESS_HEADING_TAIL = / \| (Open Positions|Positions|avointa työpaikkaa|työpaikkaa|lediga tjänster|tjänster|lediga jobb)$/

export function generateResultsHeading(filters: ActiveFilter[], count: number | null, lang: SRPLanguage): string {
  const resolved = resolveRegionTagDisplayForHeading(filters, lang)
  let core = buildTitleCore(resolved, count, lang).replace(/ \| JobCrawls$/, '').replace(/ – /g, ' | ')
  // Without a count the title templates still end in their count label ("Jobs in Oulu, Finland |
  // Open Positions"): drop that bare label so a count-less H1 is just the query phrase.
  if (count === null) core = core.replace(COUNTLESS_HEADING_TAIL, '') || core
  const extra = sortByPriority(resolved).slice(2)
  return core + buildFilterTagSuffix(extra, lang)
}

function homepageTitle(count: string | null, lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return count
      ? trunc(`Jobs in Finland – ${count} Open Positions | JobCrawls`)
      : trunc(`Jobs in Finland – Open Positions | JobCrawls`)
    case 'fi': return count
      ? trunc(`Työpaikat Suomessa – ${count} avointa työpaikkaa | JobCrawls`)
      : trunc(`Työpaikat Suomessa – avoimia työpaikkoja | JobCrawls`)
    case 'sv': return count
      ? trunc(`Jobb i Finland – ${count} lediga tjänster | JobCrawls`)
      : trunc(`Jobb i Finland – lediga tjänster | JobCrawls`)
  }
}

function singleFilterTitle(f: ActiveFilter, count: string | null, lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return singleTitleEN(f, count)
    case 'fi': return singleTitleFI(f, count)
    case 'sv': return singleTitleSV(f, count)
  }
}

function singleTitleEN(f: ActiveFilter, c: string | null): string {
  const v = f.value
  const n = c ? `${c} ` : ''
  switch (f.type) {
    case 'location':            return trunc(`Jobs in ${v} – ${n}Open Positions | JobCrawls`)
    case 'regionTags':          return trunc(`Jobs in ${v} – ${n}Open Positions | JobCrawls`)
    case 'company':             return trunc(`${v} Jobs – ${n}Open Positions | JobCrawls`)
    case 'normalisedTitle':     return trunc(`${v} Jobs in Finland – ${n}Open Positions | JobCrawls`)
    case 'field':               return trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'companyIndustry':     return trunc(`${v} Industry Jobs in Finland${c ? ` – ${c}` : ''} | JobCrawls`)
    case 'remoteness':
      return f.rawValue.toLowerCase() === 'remote'
        ? trunc(`Remote Jobs in Finland – ${n}Positions | JobCrawls`)
        : trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'requiredLanguages':   return trunc(`${capitalize(v)}-Speaking Jobs in Finland${c ? ` – ${c}` : ''} | JobCrawls`)
    case 'jobType':             return trunc(`${getEnglishJobTypeLabel(v)} in Finland – ${n}Positions | JobCrawls`)
    case 'normalisedRoleLevel': return trunc(`${getEnglishRoleLevelLabel(v)} Jobs in Finland${c ? ` – ${c}` : ''} | JobCrawls`)
    case 'skillsAndTechnologies': return trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'specialisations':       return trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'peopleManagementTier':  return trunc(`${v} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'educationLevel':        return trunc(`${getEnglishEducationLevelLabel(v)} Jobs in Finland – ${n}Positions | JobCrawls`)
    case 'benefits':              return trunc(`Jobs with ${v} in Finland – ${n}Positions | JobCrawls`)
    default:                      return trunc(`Jobs in Finland – ${n}Positions | JobCrawls`)
  }
}

function singleTitleFI(f: ActiveFilter, c: string | null): string {
  const v = f.value
  const dash = c ? ` – ${c}` : ''
  switch (f.type) {
    case 'location': {
      const city = getFinnishCityName(f.rawValue)
      return trunc(`Avoimet työpaikat ${city}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    }
    case 'regionTags':
      return trunc(`Avoimet työpaikat ${v}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    case 'company':
      return trunc(`Avoimet työpaikat ${v}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    // Genitive ("Hitsaajan työpaikat"), the form people actually search; see finnish-role-genitive.ts.
    case 'normalisedTitle':
      return trunc(`${finnishRolePhrase(v, 'työpaikat')} Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    case 'field':
      return trunc(`${getFieldGenitive(v)} työpaikat Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    case 'companyIndustry':
      return trunc(`Avoimet työpaikat ${v}${dash} | JobCrawls`)
    // "Etätyöpaikat" is the head term ("etätyöpaikat", "etätyö työpaikat"); the old
    // "Etätyömahdollisuudet" is a word nobody searches.
    case 'remoteness':
      return f.rawValue.toLowerCase() === 'remote' || f.rawValue.toLowerCase() === 'etätyö'
        ? trunc(`Etätyöpaikat Suomessa${c ? ` – ${c} avointa etätyötä` : ''} | JobCrawls`)
        : trunc(`${capitalize(getFinnishRemotenessJobNoun(v))} Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    case 'requiredLanguages': {
      const adj = getFinnishLanguageAdjective(v)
      return trunc(`${capitalize(adj)} työpaikat Suomessa${dash} | JobCrawls`)
    }
    case 'jobType':
      return trunc(`Avoimet ${getFinnishJobTypeTitle(v)}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    case 'normalisedRoleLevel':
      return trunc(`${getFinnishRoleLevelTitle(v)} työpaikat Suomessa${dash} | JobCrawls`)
    case 'skillsAndTechnologies':
    case 'specialisations':
    case 'peopleManagementTier':
      return trunc(`${v} työpaikat Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    case 'educationLevel':
      return trunc(`${capitalize(getFinnishEducationLevelTitle(v))} Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    case 'benefits':
      return trunc(`Työpaikat, joissa ${v}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    default:
      return trunc(`Työpaikat Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
  }
}

function singleTitleSV(f: ActiveFilter, c: string | null): string {
  const v = f.value
  const dash = c ? ` – ${c}` : ''
  switch (f.type) {
    case 'location': {
      // "Lediga jobb i Tammerfors": the preposition form is what people search ("lediga jobb i
      // helsingfors", "jobb i vasa") and reads as Swedish rather than a keyword list.
      const city = getSwedishCityPrep(f.rawValue)
      return trunc(`Lediga jobb ${city}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
    case 'regionTags':
      return trunc(`Lediga jobb i ${v}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'company':
      return trunc(`Lediga jobb ${v}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'normalisedTitle':
      return trunc(`${v}, lediga jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'field':
      return trunc(`Lediga jobb ${v}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'companyIndustry':
      return trunc(`Lediga jobb ${v}${dash} | JobCrawls`)
    case 'remoteness':
      return f.rawValue.toLowerCase() === 'remote' || f.rawValue.toLowerCase() === 'distans'
        ? trunc(`Distansjobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
        : trunc(`${capitalize(getSwedishRemotenessJobNoun(v))} i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'requiredLanguages': {
      const adj = getSwedishLanguageAdjective(v)
      return trunc(`${capitalize(adj)} jobb i Finland${dash} | JobCrawls`)
    }
    case 'jobType':
      return trunc(`Lediga ${getSwedishJobTypeTitle(v)}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'normalisedRoleLevel':
      return trunc(`${v}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'skillsAndTechnologies':
    case 'specialisations':
    case 'peopleManagementTier':
      return trunc(`${v}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'educationLevel':
      return trunc(`${capitalize(getSwedishEducationLevelTitle(v))} i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    case 'benefits':
      return trunc(`Jobb med ${v} i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    default:
      return trunc(`Jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
  }
}

function twoFilterTitle(
  p: ActiveFilter, s: ActiveFilter, c: string | null, lang: SRPLanguage,
): string {
  switch (lang) {
    case 'en': return twoFilterTitleEN(p, s, c)
    case 'fi': return twoFilterTitleFI(p, s, c)
    case 'sv': return twoFilterTitleSV(p, s, c)
  }
}

function twoFilterTitleEN(p: ActiveFilter, s: ActiveFilter, c: string | null): string {
  const dash = c ? ` – ${c}` : ''
  if (p.type === 'location' || p.type === 'regionTags') {
    switch (s.type) {
      case 'field':
      case 'companyIndustry':     return trunc(`${s.value} Jobs in ${p.value}${dash} | JobCrawls`)
      case 'requiredLanguages':   return trunc(`${capitalize(s.value)}-Speaking Jobs in ${p.value}${dash} | JobCrawls`)
      case 'remoteness':          return trunc(`${englishRemotenessLabel(s)} Jobs in ${p.value}${dash} | JobCrawls`)
      case 'company':             return trunc(`${s.value} Jobs in ${p.value}${dash} | JobCrawls`)
      case 'jobType':             return trunc(`${getEnglishJobTypeLabel(s.value)} in ${p.value}${dash} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${s.value} Jobs in ${p.value}${dash} | JobCrawls`)
      default:                    return trunc(`Jobs in ${p.value}${c ? ` – ${c} Open Positions` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'normalisedTitle') {
    switch (s.type) {
      case 'location':
      case 'regionTags': return trunc(`${p.value} Jobs in ${s.value}${dash} | JobCrawls`)
      case 'company':    return trunc(`${p.value} Jobs at ${s.value}${dash} | JobCrawls`)
      case 'remoteness': return trunc(`${englishRemotenessLabel(s)} ${p.value} Jobs in Finland${dash} | JobCrawls`)
      default:           return trunc(`${p.value} Jobs in Finland${dash} | JobCrawls`)
    }
  }
  if (p.type === 'company') {
    switch (s.type) {
      case 'location':
      case 'regionTags': return trunc(`${p.value} Jobs in ${s.value}${dash} | JobCrawls`)
      case 'field':    return trunc(`${p.value} ${s.value} Jobs${dash} | JobCrawls`)
      default:         return trunc(`${p.value} Jobs${c ? ` – ${c} Open Positions` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'field') {
    switch (s.type) {
      case 'location':
      case 'regionTags':        return trunc(`${p.value} Jobs in ${s.value}${dash} | JobCrawls`)
      case 'remoteness':        return trunc(`${englishRemotenessLabel(s)} ${p.value} Jobs in Finland${dash} | JobCrawls`)
      case 'requiredLanguages': return trunc(`${capitalize(s.value)}-Speaking ${p.value} Jobs in Finland${dash} | JobCrawls`)
      default:                  return trunc(`${p.value} Jobs in Finland${dash} | JobCrawls`)
    }
  }
  if (p.type === 'remoteness') {
    const isRemote = p.rawValue.toLowerCase() === 'remote'
    const lbl = isRemote ? 'Remote' : capitalize(p.value)
    switch (s.type) {
      case 'requiredLanguages':   return trunc(`${capitalize(s.value)}-Speaking ${lbl} Jobs in Finland${dash} | JobCrawls`)
      case 'field':               return trunc(`${lbl} ${s.value} Jobs in Finland${dash} | JobCrawls`)
      case 'jobType':             return trunc(`${lbl} ${getEnglishJobTypeLabel(s.value)} in Finland${dash} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${lbl} ${getEnglishRoleLevelLabel(s.value)} Jobs in Finland${dash} | JobCrawls`)
      case 'companyIndustry':     return trunc(`${lbl} ${s.value} Industry Jobs in Finland${dash} | JobCrawls`)
      default:                    return trunc(`${lbl} Jobs in Finland${dash} | JobCrawls`)
    }
  }
  if (p.type === 'requiredLanguages') {
    const spk = `${capitalize(p.value)}-Speaking`
    switch (s.type) {
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote'
        return trunc(`${spk} ${isRemote ? 'Remote' : capitalize(s.value)} Jobs in Finland${dash} | JobCrawls`)
      }
      case 'field':               return trunc(`${spk} ${s.value} Jobs in Finland${dash} | JobCrawls`)
      case 'jobType':             return trunc(`${spk} ${getEnglishJobTypeLabel(s.value)} in Finland${dash} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${spk} ${getEnglishRoleLevelLabel(s.value)} Jobs in Finland${dash} | JobCrawls`)
      case 'companyIndustry':     return trunc(`${spk} ${s.value} Industry Jobs in Finland${dash} | JobCrawls`)
      default:                    return trunc(`${spk} Jobs in Finland${dash} | JobCrawls`)
    }
  }
  return trunc(`${capitalize(p.value)} ${s.value} Jobs in Finland${dash} | JobCrawls`)
}

function twoFilterTitleFI(p: ActiveFilter, s: ActiveFilter, c: string | null): string {
  const dash = c ? ` – ${c}` : ''
  if (p.type === 'location') {
    const loc = getFinnishCityName(p.rawValue)
    switch (s.type) {
      case 'field':
        return trunc(`${getFieldGenitive(s.value)} työpaikat ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'companyIndustry':
        return trunc(`Avoimet työpaikat ${s.value}, ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'requiredLanguages': {
        const adj = getFinnishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} työpaikat ${loc}${dash} | JobCrawls`)
      }
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'etätyö'
        // "Hybridityöpaikat Tampereella": inessive when curated, bare town name otherwise.
        const locIn = getCityInessiveOrNull(p.rawValue) ?? loc
        return isRemote
          ? trunc(`Etätyöpaikat ${locIn}${c ? ` – ${c} avointa etätyötä` : ''} | JobCrawls`)
          : trunc(`${capitalize(getFinnishRemotenessJobNoun(s.value))} ${locIn}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      }
      case 'company':
        return trunc(`Avoimet työpaikat ${s.value}, ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'jobType':
        return trunc(`${capitalize(getFinnishJobTypeTitle(s.value))} ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'normalisedRoleLevel':
        return trunc(`${getFinnishRoleLevelTitle(s.value)} työpaikat ${loc}${dash} | JobCrawls`)
      default:
        return trunc(`Avoimet työpaikat ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'regionTags') {
    const loc = p.value
    switch (s.type) {
      case 'field':
        return trunc(`${getFieldGenitive(s.value)} työpaikat ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'companyIndustry':
        return trunc(`Avoimet työpaikat ${s.value}, ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'requiredLanguages': {
        const adj = getFinnishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} työpaikat ${loc}${dash} | JobCrawls`)
      }
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'etätyö'
        return isRemote
          ? trunc(`Etätyöpaikat ${loc}${c ? ` – ${c} avointa etätyötä` : ''} | JobCrawls`)
          : trunc(`${capitalize(getFinnishRemotenessJobNoun(s.value))} ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      }
      case 'company':
        return trunc(`Avoimet työpaikat ${s.value}, ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'jobType':
        return trunc(`${capitalize(getFinnishJobTypeTitle(s.value))} ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'normalisedRoleLevel':
        return trunc(`${getFinnishRoleLevelTitle(s.value)} työpaikat ${loc}${dash} | JobCrawls`)
      default:
        return trunc(`Avoimet työpaikat ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'normalisedTitle') {
    const roleJobs = finnishRolePhrase(p.value, 'työpaikat')
    switch (s.type) {
      case 'location': {
        // "Hitsaajan työpaikat Tampereella"; nominative fallback for towns without a curated form.
        const loc = getCityInessiveOrNull(s.rawValue) ?? getFinnishCityName(s.rawValue)
        return trunc(`${roleJobs} ${loc}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      }
      case 'regionTags':
        return trunc(`${roleJobs} ${s.value}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      case 'company':
        return trunc(`${roleJobs}, ${s.value}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'etätyö'
        // "Sairaanhoitajan etätyöt" / "Sairaanhoitajan hybridityöt": the query shape ("etätyö sairaanhoitaja").
        return isRemote
          ? trunc(`${finnishRolePhrase(p.value, 'etätyöt')}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
          : s.rawValue.toLowerCase() === 'hybridityö' || s.rawValue.toLowerCase() === 'hybrid'
            ? trunc(`${finnishRolePhrase(p.value, 'hybridityöt')}${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
            : trunc(`${roleJobs} Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
      }
      default:
        return trunc(`${roleJobs} Suomessa${c ? ` – ${c} paikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'company') {
    switch (s.type) {
      case 'location': {
        const loc = getFinnishCityName(s.rawValue)
        return trunc(`Avoimet työpaikat ${p.value}, ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      }
      case 'regionTags':
        return trunc(`Avoimet työpaikat ${p.value}, ${s.value}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'field':
        return trunc(`Avoimet työpaikat ${p.value}, ${s.value}${dash} | JobCrawls`)
      default:
        return trunc(`Avoimet työpaikat ${p.value}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'field') {
    const gen = getFieldGenitive(p.value)
    switch (s.type) {
      case 'location': {
        const loc = getFinnishCityName(s.rawValue)
        return trunc(`${gen} työpaikat ${loc}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      }
      case 'regionTags':
        return trunc(`${gen} työpaikat ${s.value}${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'etätyö'
        return isRemote
          ? trunc(`${gen} etätyöpaikat Suomessa${dash} | JobCrawls`)
          : trunc(`${gen} ${getFinnishRemotenessJobNoun(s.value).toLowerCase()} Suomessa${dash} | JobCrawls`)
      }
      case 'requiredLanguages': {
        const adj = getFinnishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} ${gen.toLowerCase()} työpaikat${dash} | JobCrawls`)
      }
      default:
        return trunc(`${gen} työpaikat Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'remoteness') {
    const isRemote = p.rawValue.toLowerCase() === 'remote' || p.rawValue.toLowerCase() === 'etätyö'
    switch (s.type) {
      case 'requiredLanguages': {
        const adj = getFinnishLanguageAdjective(s.value)
        return isRemote
          ? trunc(`${capitalize(adj)} etätyömahdollisuudet Suomessa${dash} | JobCrawls`)
          : trunc(`${capitalize(adj)} ${getFinnishRemotenessJobNoun(p.value).toLowerCase()} Suomessa${dash} | JobCrawls`)
      }
      case 'field': {
        const gen = getFieldGenitive(s.value)
        return isRemote
          ? trunc(`${gen} etätyömahdollisuudet Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
          : trunc(`${gen} ${getFinnishRemotenessJobNoun(p.value).toLowerCase()} Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
      }
      default:
        return isRemote
          ? trunc(`Etätyömahdollisuudet Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
          : trunc(`${capitalize(getFinnishRemotenessJobNoun(p.value))} Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'requiredLanguages') {
    const adj = getFinnishLanguageAdjective(p.value)
    switch (s.type) {
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'etätyö'
        return isRemote
          ? trunc(`${capitalize(adj)} etätyömahdollisuudet Suomessa${dash} | JobCrawls`)
          : trunc(`${capitalize(adj)} ${getFinnishRemotenessJobNoun(s.value).toLowerCase()} Suomessa${dash} | JobCrawls`)
      }
      case 'field': {
        const gen = getFieldGenitive(s.value)
        return trunc(`${capitalize(adj)} ${gen.toLowerCase()} työpaikat${dash} | JobCrawls`)
      }
      default:
        return trunc(`${capitalize(adj)} työpaikat Suomessa${dash} | JobCrawls`)
    }
  }
  return trunc(`${capitalize(p.value)} ${s.value} työpaikat Suomessa${c ? ` – ${c} työpaikkaa` : ''} | JobCrawls`)
}

function twoFilterTitleSV(p: ActiveFilter, s: ActiveFilter, c: string | null): string {
  const dash = c ? ` – ${c}` : ''
  if (p.type === 'location') {
    const loc = getSwedishCityName(p.rawValue)
    switch (s.type) {
      case 'field':
      case 'companyIndustry':     return trunc(`Lediga jobb ${s.value}, ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'requiredLanguages': {
        const adj = getSwedishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} jobb ${loc}${dash} | JobCrawls`)
      }
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'distans'
        return isRemote
          ? trunc(`Distansjobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
          : trunc(`${capitalize(getSwedishRemotenessJobNoun(s.value))} ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      }
      case 'company':             return trunc(`Lediga jobb ${s.value}, ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'jobType':             return trunc(`${capitalize(getSwedishJobTypeTitle(s.value))} ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${s.value}-jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:                    return trunc(`Lediga jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'regionTags') {
    const loc = p.value
    switch (s.type) {
      case 'field':
      case 'companyIndustry':     return trunc(`Lediga jobb ${s.value} i ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'requiredLanguages': {
        const adj = getSwedishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} jobb i ${loc}${dash} | JobCrawls`)
      }
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'distans'
        return isRemote
          ? trunc(`Distansjobb i ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
          : trunc(`${capitalize(getSwedishRemotenessJobNoun(s.value))} i ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      }
      case 'company':             return trunc(`Lediga jobb ${s.value} i ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'jobType':             return trunc(`${capitalize(getSwedishJobTypeTitle(s.value))} i ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'normalisedRoleLevel': return trunc(`${s.value}-jobb i ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:                    return trunc(`Lediga jobb i ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'normalisedTitle') {
    switch (s.type) {
      case 'location': {
        const loc = getSwedishCityName(s.rawValue)
        return trunc(`${p.value}, lediga jobb ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      }
      case 'regionTags':
        return trunc(`${p.value}, lediga jobb i ${s.value}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'company':
        return trunc(`${p.value}, lediga jobb hos ${s.value}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:
        return trunc(`${p.value}, lediga jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'company') {
    switch (s.type) {
      case 'location': {
        const loc = getSwedishCityName(s.rawValue)
        return trunc(`Lediga jobb ${p.value}, ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      }
      case 'regionTags':
        return trunc(`Lediga jobb ${p.value}, ${s.value}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'field':
        return trunc(`Lediga jobb ${p.value}, ${s.value}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:
        return trunc(`Lediga jobb ${p.value}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'field') {
    switch (s.type) {
      case 'location': {
        const loc = getSwedishCityName(s.rawValue)
        return trunc(`Lediga jobb ${p.value}, ${loc}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      }
      case 'regionTags':
        return trunc(`Lediga jobb ${p.value}, ${s.value}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'distans'
        return isRemote
          ? trunc(`${p.value} distansjobb i Finland${dash} | JobCrawls`)
          : trunc(`${p.value} ${getSwedishRemotenessJobNoun(s.value)} i Finland${dash} | JobCrawls`)
      }
      case 'requiredLanguages': {
        const adj = getSwedishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} ${p.value.toLowerCase()}-jobb${dash} | JobCrawls`)
      }
      default:
        return trunc(`Lediga jobb ${p.value}${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'remoteness') {
    const isRemote = p.rawValue.toLowerCase() === 'remote' || p.rawValue.toLowerCase() === 'distans'
    const jobNoun = getSwedishRemotenessJobNoun(p.value)
    switch (s.type) {
      case 'requiredLanguages': {
        const adj = getSwedishLanguageAdjective(s.value)
        return trunc(`${capitalize(adj)} ${jobNoun} i Finland${dash} | JobCrawls`)
      }
      case 'field':
        return trunc(`${s.value} ${jobNoun} i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
      default:
        return isRemote
          ? trunc(`Distansjobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
          : trunc(`${capitalize(jobNoun)} i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
    }
  }
  if (p.type === 'requiredLanguages') {
    const adj = getSwedishLanguageAdjective(p.value)
    switch (s.type) {
      case 'remoteness': {
        const isRemote = s.rawValue.toLowerCase() === 'remote' || s.rawValue.toLowerCase() === 'distans'
        return isRemote
          ? trunc(`${capitalize(adj)} distansjobb i Finland${dash} | JobCrawls`)
          : trunc(`${capitalize(adj)} ${getSwedishRemotenessJobNoun(s.value)} i Finland${dash} | JobCrawls`)
      }
      case 'field':
        return trunc(`${capitalize(adj)} ${s.value.toLowerCase()}-jobb i Finland${dash} | JobCrawls`)
      default:
        return trunc(`${capitalize(adj)} jobb i Finland${dash} | JobCrawls`)
    }
  }
  return trunc(`${capitalize(p.value)} ${s.value}-jobb i Finland${c ? ` – ${c} tjänster` : ''} | JobCrawls`)
}

/**
 * Localized count noun for the trailing "N jobs" clause on multi-filter titles, matching the
 * phrasing the single-filter titles already use so the count reads consistently across titles.
 */
function countClause(c: string, lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return `${c} Open Positions`
    case 'fi': return `${c} työpaikkaa`
    case 'sv': return `${c} tjänster`
  }
}

/**
 * 3+ filters: the top two keep their natural-language combination (twoFilterTitle) and every
 * remaining filter is folded in as the same compact, localized tag list the on-page H1 uses
 * (buildFilterTagSuffix). Pieces are added in priority order — head, then extra-filter tags, then
 * the job count — and dropped in reverse (count first, then tags) when the 60-char budget is
 * exceeded, so the count only appears when there is room and the brand suffix is never dropped.
 */
function multiFilterTitle(sorted: ActiveFilter[], c: string | null, lang: SRPLanguage): string {
  const [p, s] = sorted
  const headWithBrand = twoFilterTitle(p, s, null, lang)
  const head = headWithBrand.replace(/\s\|\sJobcrawls\s*$/i, '')
  const tags = buildFilterTagSuffix(sorted.slice(2), lang)
  const count = c ? ` – ${countClause(c, lang)}` : ''

  const brand = ' | JobCrawls'
  const withCount = `${head}${tags}${count}${brand}`
  if (withCount.length <= DEFAULT_SEO_TITLE_MAX_LENGTH) return withCount

  const withoutCount = `${head}${tags}${brand}`
  if (withoutCount.length <= DEFAULT_SEO_TITLE_MAX_LENGTH) return withoutCount

  const withoutTags = `${head}${brand}`
  if (withoutTags.length <= DEFAULT_SEO_TITLE_MAX_LENGTH) return withoutTags

  return truncateSeoTitle(`${head}${brand}`, DEFAULT_SEO_TITLE_MAX_LENGTH)
}

// ============================================================================
// DESCRIPTION GENERATION
// ============================================================================

/**
 * Generates a meta description that is ALWAYS ≥140 characters.
 *
 * Strategy:
 * 1. Build primary facts from available data (salary, employers, etc.)
 * 2. Build secondary/fallback facts (skills, benefits, salary range, job types)
 * 3. Combine facts until we hit the 140-160 char target
 * 4. If still short, append a branded CTA
 */
export function generateDescription(
  filters: ActiveFilter[],
  data: AggregatedJobData,
  lang: SRPLanguage,
): string {
  const active = resolveRegionTagDisplay(filters, lang)
  const filterTypes = new Set(active.map(f => f.type))
  const facts: string[] = []
  const fallbackFacts: string[] = []

  facts.push(buildOpener(active, data.count, lang))
  const isLocationPage = (filterTypes.has('location') || filterTypes.has('regionTags')) && !filterTypes.has('normalisedTitle')
  // Location pages: the sub-intents behind "työpaikat tampere" are roles ("tampere työpaikat
  // lähihoitaja / myyjä"), and for English searchers the English-speaking share ("jobs in
  // helsinki for english speakers"), so both come right after the opener.
  if (isLocationPage && lang === 'en' && data.englishPct != null && data.englishPct > 0) {
    facts.push(pctFact(data.englishPct, label('english', lang), lang))
  }
  if (isLocationPage && data.topRoles && data.topRoles.length > 0) {
    // fi/sv job titles are lowercase mid-sentence ("ammatit: lähihoitaja, myyjä").
    const roles = data.topRoles.slice(0, 3).map((r) => (lang === 'en' ? r : r.toLowerCase()))
    facts.push(listFact(label('roles', lang), roles))
  }

  // Maakunta (admin1) region pages: "covers N municipalities" reads naturally right after the
  // opener and gives Google a concrete, page-specific fact a plain city page doesn't have.
  const regionAdmin1Filter = active.find((f) => f.type === 'regionTags' && getRegionTagType(f.rawValue) === 'admin1')
  if (regionAdmin1Filter) {
    const fact = regionMunicipalityFact(regionAdmin1Filter.rawValue, lang)
    if (fact) facts.push(fact)
  }

  // sv: "svenska jobb {stad}" is the language-specific intent, so the Swedish share precedes salary.
  if (isLocationPage && lang === 'sv' && data.swedishPct != null && data.swedishPct > 0) {
    facts.push(pctFact(data.swedishPct, label('swedish', lang), lang))
  }
  if (data.avgSalary) {
    facts.push(salaryFact(data.avgSalary, lang))
  }

  if (!filterTypes.has('company') && data.topEmployers.length > 0) {
    facts.push(listFact(label('employers', lang), data.topEmployers.slice(0, 3)))
  }

  if (!filterTypes.has('location') && !filterTypes.has('regionTags') && data.topCities.length > 0) {
    facts.push(listFact(label('cities', lang), data.topCities.slice(0, 3)))
  }

  if (!(isLocationPage && lang === 'en') && !filterTypes.has('requiredLanguages') && data.englishPct != null && data.englishPct > 0) {
    facts.push(pctFact(data.englishPct, label('english', lang), lang))
  }

  if (!filterTypes.has('remoteness') && data.remotePct != null && data.remotePct > 0) {
    facts.push(pctFact(data.remotePct, label('remote', lang), lang))
  }

  if (!filterTypes.has('field') && !filterTypes.has('companyIndustry') && data.topFields.length > 0) {
    facts.push(listFact(label('fields', lang), data.topFields.slice(0, 3)))
  }

  if (!filterTypes.has('jobType') && data.fullTimePct != null) {
    fallbackFacts.push(pctFact(data.fullTimePct, label('fulltime', lang), lang))
  }

  if (data.salaryRangeLabel) {
    fallbackFacts.push(rangeFact(data.salaryRangeLabel, lang))
  }

  if (data.topSkills.length > 0) {
    fallbackFacts.push(listFact(label('skills', lang), data.topSkills.slice(0, 3)))
  }

  if (data.topBenefits.length > 0) {
    fallbackFacts.push(listFact(label('benefits', lang), data.topBenefits.slice(0, 3)))
  }

  const cta = ctaText(lang)

  let desc = facts.map(sentence).filter(Boolean).join(' ')

  // Only append a candidate clause whole if it fits within MAX_DESCRIPTION_LENGTH — appending past
  // the cap and truncating the whole string afterwards is what produced descriptions cut mid-sentence
  // (e.g. "...Listings reflect"). Clauses that don't fit are tracked so that, if the MIN length still
  // isn't met once every candidate has been tried, exactly one of them can be shortened to fit — as a
  // controlled, word-boundary, ellipsis-marked cut, not an arbitrary mid-word chop of the whole string.
  const skipped: string[] = []
  const tryAppend = (candidate: string): boolean => {
    const next = `${desc} ${candidate}`
    if (next.length > MAX_DESCRIPTION_LENGTH) {
      skipped.push(candidate)
      return false
    }
    desc = next
    return true
  }

  for (const fact of fallbackFacts) {
    if (desc.length >= MIN_DESCRIPTION_LENGTH) break
    tryAppend(sentence(fact))
  }

  if (desc.length < MIN_DESCRIPTION_LENGTH) {
    tryAppend(cta)
  }

  for (const extra of extraDescriptionClauses(lang)) {
    if (desc.length >= MIN_DESCRIPTION_LENGTH) break
    tryAppend(extra)
  }

  if (desc.length < MIN_DESCRIPTION_LENGTH) {
    tryAppend(cta)
  }

  if (desc.length < MIN_DESCRIPTION_LENGTH && skipped.length > 0) {
    const remaining = MAX_DESCRIPTION_LENGTH - desc.length - 1
    const cut = skipped[0].substring(0, remaining)
    const lastSpace = cut.lastIndexOf(' ')
    const piece = lastSpace > 10 ? cut.substring(0, lastSpace) : cut
    if (piece.length > 10) {
      desc = `${desc} ${piece}...`
    }
  }

  if (desc.length > MAX_DESCRIPTION_LENGTH) {
    desc = truncDesc(desc, MAX_DESCRIPTION_LENGTH - 3)
  }

  return desc
}

/** 'remote' | 'hybrid' when a secondary remoteness filter narrows the page (remote x field, hybrid x city), else null. */
function secondaryWorkModel(sorted: ActiveFilter[]): 'remote' | 'hybrid' | null {
  const r = sorted.slice(1).find((f) => f.type === 'remoteness')
  if (!r) return null
  const v = r.rawValue.toLowerCase()
  if (v === 'remote' || v === 'etätyö' || v === 'distans') return 'remote'
  if (v === 'hybrid' || v === 'hybridityö') return 'hybrid'
  return null
}

function buildOpener(filters: ActiveFilter[], count: number | null, lang: SRPLanguage): string {
  const c = count != null && count > 0 ? fmtCount(count, lang) : null
  const sorted = sortByPriority(filters)
  const p = sorted[0]
  // Work-model combination pages say what they are ("etätyöpaikkaa", "remote jobs") in the
  // opener, since the primary filter alone would describe the plain field/city/role page.
  const wm = secondaryWorkModel(sorted)
  const fiJobNoun = wm === 'remote' ? 'etätyöpaikkaa' : wm === 'hybrid' ? 'hybridityöpaikkaa' : 'työpaikkaa'
  const enJobWord = wm === 'remote' ? 'remote ' : wm === 'hybrid' ? 'hybrid ' : ''
  const svJobNoun = wm === 'remote' ? 'distansjobb' : wm === 'hybrid' ? 'hybridjobb' : 'lediga jobb'

  if (!p) {
    switch (lang) {
      case 'en': return c ? `Browse ${c} open jobs in Finland` : `Browse open jobs in Finland`
      case 'fi': return c ? `Selaa ${c} avointa työpaikkaa Suomessa` : `Selaa avointa työpaikkaa Suomessa`
      case 'sv': return c ? `Bläddra bland ${c} lediga jobb i Finland` : `Bläddra bland lediga jobb i Finland`
    }
  }

  switch (lang) {
    case 'en':
      switch (p.type) {
        case 'location':
          return c
            ? `Browse ${c} open ${enJobWord}jobs in ${p.value}, straight from employers and Työmarkkinatori`
            : `Browse open ${enJobWord}jobs in ${p.value}, straight from employers and Työmarkkinatori`
        case 'regionTags':
          return c
            ? `Browse ${c} open ${enJobWord}jobs in ${p.value}, straight from employers and Työmarkkinatori`
            : `Browse open ${enJobWord}jobs in ${p.value}, straight from employers and Työmarkkinatori`
        case 'normalisedTitle':
          return c ? `Browse ${c} open ${p.value.toLowerCase()} jobs in Finland` : `Browse open ${p.value.toLowerCase()} jobs in Finland`
        case 'company':
          return c ? `${p.value} has ${c} open positions` : `${p.value} has open positions`
        case 'field':
          return c ? `Find ${c} ${enJobWord}${p.value.toLowerCase()} jobs in Finland` : `Find ${enJobWord}${p.value.toLowerCase()} jobs in Finland`
        case 'remoteness': {
          const remote = p.rawValue.toLowerCase() === 'remote' ? 'remote' : p.value.toLowerCase()
          return c ? `Browse ${c} ${remote} jobs in Finland` : `Browse ${remote} jobs in Finland`
        }
        case 'requiredLanguages':
          return c
            ? `Find ${c} ${capitalize(p.value)}-speaking jobs in Finland`
            : `Find ${capitalize(p.value)}-speaking jobs in Finland`
        case 'jobType':
          return c
            ? `Browse ${c} ${getEnglishJobTypeLabelLower(p.value)} in Finland`
            : `Browse ${getEnglishJobTypeLabelLower(p.value)} in Finland`
        case 'companyIndustry':
          return c
            ? `Browse ${c} open ${p.value.toLowerCase()} industry jobs in Finland`
            : `Browse open ${p.value.toLowerCase()} industry jobs in Finland`
        case 'normalisedRoleLevel':
          return c
            ? `Browse ${c} open ${getEnglishRoleLevelLabel(p.value).toLowerCase()} jobs in Finland`
            : `Browse open ${getEnglishRoleLevelLabel(p.value).toLowerCase()} jobs in Finland`
        case 'educationLevel':
          return c
            ? `Browse ${c} open ${getEnglishEducationLevelLabel(p.value).toLowerCase()} jobs in Finland`
            : `Browse open ${getEnglishEducationLevelLabel(p.value).toLowerCase()} jobs in Finland`
        case 'skillsAndTechnologies':
        case 'specialisations':
        case 'peopleManagementTier':
          return c
            ? `Browse ${c} open ${p.value.toLowerCase()} jobs in Finland`
            : `Browse open ${p.value.toLowerCase()} jobs in Finland`
        case 'benefits':
          return c
            ? `Browse ${c} open jobs with ${p.value.toLowerCase()} in Finland`
            : `Browse open jobs with ${p.value.toLowerCase()} in Finland`
        default:
          return c ? `Browse ${c} open jobs in Finland` : `Browse open jobs in Finland`
      }
    case 'fi':
      switch (p.type) {
        case 'location': {
          const loc = getCityInessiveOrNull(p.rawValue)
          if (loc) {
            return c
              ? `Selaa ${c} avointa ${fiJobNoun} ${loc}, suoraan työnantajilta ja Työmarkkinatorilta`
              : `Selaa avoimia ${fiJobNoun.replace(/paikkaa$/, 'paikkoja')} ${loc}, suoraan työnantajilta ja Työmarkkinatorilta`
          }
          // No vetted case form: lead with the bare town name and a colon, which is natural
          // Finnish for a listing and never wrong, instead of guessing -ssa vs -lla.
          const town = getFinnishCityName(p.rawValue)
          return c
            ? `${town}: selaa ${c} avointa työpaikkaa suoraan työnantajilta ja Työmarkkinatorilta`
            : `${town}: selaa avoimia työpaikkoja suoraan työnantajilta ja Työmarkkinatorilta`
        }
        case 'regionTags':
          return c
            ? `${p.value}: selaa ${c} avointa työpaikkaa suoraan työnantajilta ja Työmarkkinatorilta`
            : `${p.value}: selaa avoimia työpaikkoja suoraan työnantajilta ja Työmarkkinatorilta`
        case 'normalisedTitle': {
          return c
            ? `Selaa ${c} avointa ${finnishRolePhrase(p.value, fiJobNoun, true)} Suomessa`
            : `Selaa avoimia ${finnishRolePhrase(p.value, fiJobNoun.replace(/paikkaa$/, 'paikkoja'), true)} Suomessa`
        }
        case 'company':
          return c ? `${p.value} tarjoaa ${c} avointa työpaikkaa` : `${p.value} tarjoaa avoimia työpaikkoja`
        case 'field': {
          const gen = getFieldGenitive(p.value)
          return c ? `Löydä ${c} ${gen.toLowerCase()} ${fiJobNoun} Suomessa` : `Löydä ${gen.toLowerCase()} ${fiJobNoun} Suomessa`
        }
        case 'remoteness': {
          const isRemote = p.rawValue.toLowerCase() === 'remote' || p.rawValue.toLowerCase() === 'etätyö'
          const isHybrid = p.rawValue.toLowerCase() === 'hybridityö' || p.rawValue.toLowerCase() === 'hybrid'
          if (isRemote) {
            return c
              ? `Selaa ${c} avointa etätyöpaikkaa Suomessa suoraan työnantajilta ja Työmarkkinatorilta`
              : `Selaa avoimia etätyöpaikkoja Suomessa suoraan työnantajilta ja Työmarkkinatorilta`
          }
          if (isHybrid) {
            return c
              ? `Selaa ${c} avointa hybridityöpaikkaa Suomessa suoraan työnantajilta ja Työmarkkinatorilta`
              : `Selaa avoimia hybridityöpaikkoja Suomessa suoraan työnantajilta ja Työmarkkinatorilta`
          }
          return c ? `Selaa ${c} avointa työpaikkaa Suomessa` : `Selaa avoimia työpaikkoja Suomessa`
        }
        case 'requiredLanguages': {
          const adj = getFinnishLanguageAdjective(p.value)
          return c
            ? `Löydä ${c} ${adj.toLowerCase()} työpaikkaa Suomessa`
            : `Löydä ${adj.toLowerCase()} työpaikkaa Suomessa`
        }
        case 'companyIndustry':
          return c
            ? `Selaa ${c} ${getFieldGenitive(p.value).toLowerCase()} ${fiJobNoun} Suomessa`
            : `Selaa ${getFieldGenitive(p.value).toLowerCase()} ${fiJobNoun} Suomessa`
        case 'normalisedRoleLevel':
          return c
            ? `Selaa ${c} ${getFinnishRoleLevelTitle(p.value).toLowerCase()} ${fiJobNoun} Suomessa`
            : `Selaa ${getFinnishRoleLevelTitle(p.value).toLowerCase()} ${fiJobNoun} Suomessa`
        case 'educationLevel': {
          const forms = getFinnishEducationLevelDescription(p.value)
          return c
            ? `Selaa ${c} ${forms.singular} Suomessa`
            : `Selaa ${forms.plural} Suomessa`
        }
        case 'skillsAndTechnologies':
        case 'specialisations':
        case 'peopleManagementTier':
          return c
            ? `Selaa ${c} ${p.value.toLowerCase()} ${fiJobNoun} Suomessa`
            : `Selaa ${p.value.toLowerCase()} ${fiJobNoun.replace(/paikkaa$/, 'paikkoja')} Suomessa`
        case 'jobType': {
          const forms = getFinnishJobTypeDescription(p.value)
          return c
            ? `Selaa ${c} ${forms.singular} Suomessa`
            : `Selaa ${forms.plural} Suomessa`
        }
        case 'benefits':
          return c
            ? `Selaa ${c} ${fiJobNoun}, joissa on ${p.value.toLowerCase()}, Suomessa`
            : `Selaa ${fiJobNoun.replace(/paikkaa$/, 'paikkoja')}, joissa on ${p.value.toLowerCase()}, Suomessa`
        default:
          return c ? `Selaa ${c} avointa työpaikkaa Suomessa` : `Selaa avointa työpaikkaa Suomessa`
      }
    case 'sv':
      switch (p.type) {
        case 'location': {
          const loc = getSwedishCityPrep(p.rawValue)
          return c
            ? `Bläddra bland ${c} ${svJobNoun} ${loc}, direkt från arbetsgivare och Työmarkkinatori`
            : `Bläddra bland ${svJobNoun} ${loc}, direkt från arbetsgivare och Työmarkkinatori`
        }
        case 'regionTags':
          return c
            ? `Bläddra bland ${c} ${svJobNoun} i ${p.value}, direkt från arbetsgivare och Työmarkkinatori`
            : `Bläddra bland ${svJobNoun} i ${p.value}, direkt från arbetsgivare och Työmarkkinatori`
        case 'normalisedTitle':
          return c ? `Bläddra bland ${c} lediga jobb som ${p.value.toLowerCase()} i Finland` : `Bläddra bland lediga jobb som ${p.value.toLowerCase()} i Finland`
        case 'company':
          return c ? `${p.value} har ${c} lediga tjänster` : `${p.value} har lediga tjänster`
        case 'field':
          return c ? `Hitta ${c} ${p.value.toLowerCase()}-${svJobNoun === 'lediga jobb' ? 'jobb' : svJobNoun} i Finland` : `Hitta ${p.value.toLowerCase()}-${svJobNoun === 'lediga jobb' ? 'jobb' : svJobNoun} i Finland`
        case 'remoteness': {
          const jobNoun = getSwedishRemotenessJobNoun(p.value)
          return c ? `Bläddra bland ${c} ${jobNoun} i Finland` : `Bläddra bland ${jobNoun} i Finland`
        }
        case 'requiredLanguages': {
          const adj = getSwedishLanguageAdjective(p.value)
          return c ? `Hitta ${c} ${adj.toLowerCase()} jobb i Finland` : `Hitta ${adj.toLowerCase()} jobb i Finland`
        }
        case 'jobType':
          return c
            ? `Bläddra bland ${c} ${getSwedishJobTypeTitle(p.value)} i Finland`
            : `Bläddra bland ${getSwedishJobTypeTitle(p.value)} i Finland`
        case 'companyIndustry':
          return c
            ? `Bläddra bland ${c} ${p.value.toLowerCase()}-jobb i Finland`
            : `Bläddra bland ${p.value.toLowerCase()}-jobb i Finland`
        case 'normalisedRoleLevel':
          return c
            ? `Bläddra bland ${c} ${p.value.toLowerCase()}-jobb i Finland`
            : `Bläddra bland ${p.value.toLowerCase()}-jobb i Finland`
        case 'educationLevel':
          return c
            ? `Bläddra bland ${c} ${getSwedishEducationLevelTitle(p.value)} i Finland`
            : `Bläddra bland ${getSwedishEducationLevelTitle(p.value)} i Finland`
        case 'skillsAndTechnologies':
        case 'specialisations':
        case 'peopleManagementTier':
          return c
            ? `Bläddra bland ${c} ${p.value.toLowerCase()}-jobb i Finland`
            : `Bläddra bland ${p.value.toLowerCase()}-jobb i Finland`
        case 'benefits':
          return c
            ? `Bläddra bland ${c} jobb med ${p.value.toLowerCase()} i Finland`
            : `Bläddra bland jobb med ${p.value.toLowerCase()} i Finland`
        default:
          return c ? `Bläddra bland ${c} lediga jobb i Finland` : `Bläddra bland lediga jobb i Finland`
      }
  }
}

/** Number-then-euro-sign is the native fi/sv convention ("4 200 €/kk"); en keeps "€4,200/mo". */
function salaryFact(salary: number, lang: SRPLanguage): string {
  const s = fmtSalary(salary, lang)
  switch (lang) {
    case 'en': return `average salary €${s}/mo`
    case 'fi': return `keskipalkka ${s} €/kk`
    case 'sv': return `genomsnittslön ${s} €/mån`
  }
}

function listFact(lbl: string, items: string[]): string {
  return `${lbl}: ${items.join(', ')}`
}

/** fi/sv put a space before the percent sign ("42 %"); en does not ("42%"). */
function pctFact(pct: number, lbl: string, lang: SRPLanguage): string {
  return lang === 'en' ? `${pct}% ${lbl}` : `${pct} % ${lbl}`
}

/**
 * Turns a fact fragment into a sentence: capital first letter, exactly one trailing full stop.
 * Facts are authored lowercase so they can also be embedded mid-sentence, but joined with ". "
 * they rendered as "…Laitila. keskipalkka €2,912/kk. työnantajat: …", and an employer name that
 * already ends in a period ("Nebius B.V.") picked up a second one.
 */
function sentence(fragment: string): string {
  const t = fragment.trim()
  if (!t) return ''
  const withCap = t.charAt(0).toUpperCase() + t.slice(1)
  return /[.!?…]$/.test(withCap) ? withCap : `${withCap}.`
}

function rangeFact(rangeLabel: string, lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return `salary range ${rangeLabel}`
    case 'fi': return `palkkahaarukka ${rangeLabel}`
    case 'sv': return `lönespann ${rangeLabel}`
  }
}

function ctaText(lang: SRPLanguage): string {
  switch (lang) {
    case 'en': return 'Filter by salary, language and remote work on JobCrawls.'
    case 'fi': return 'Suodata palkan, kielen ja etätyön mukaan JobCrawlsissa.'
    case 'sv': return 'Filtrera efter lön, språk och distansarbete på JobCrawls.'
  }
}

/** Extra clauses when facets/hits are sparse so meta length still meets {@link MIN_DESCRIPTION_LENGTH}. */
function extraDescriptionClauses(lang: SRPLanguage): string[] {
  switch (lang) {
    case 'en':
      return [
        'Refine by industry, skills and employer.',
        'Listings reflect open roles from companies hiring in Finland.',
      ]
    case 'fi':
      return [
        'Tarkenna toimialan, osaamisen ja työnantajan mukaan.',
        'Ilmoitukset kokoavat avoimet roolit suomalaisilta työnantajilta.',
      ]
    case 'sv':
      return [
        'Förfina efter bransch, kompetens och arbetsgivare.',
        'Annonserna samlar lediga roller från företag som rekryterar i Finland.',
      ]
  }
}

function label(key: string, lang: SRPLanguage): string {
  const labels: Record<string, Record<SRPLanguage, string>> = {
    employers: { en: 'top employers',  fi: 'työnantajat',      sv: 'arbetsgivare' },
    cities:    { en: 'top cities',     fi: 'kaupungit',        sv: 'städer' },
    fields:    { en: 'top fields',     fi: 'toimialat',        sv: 'branscher' },
    roles:     { en: 'roles',          fi: 'ammatit',          sv: 'yrken' },
    swedish:   { en: 'Swedish-speaking roles', fi: 'ruotsinkielisiä työpaikkoja', sv: 'svenskspråkiga jobb' },
    english:   { en: 'of roles in English', fi: 'rooleista englanniksi', sv: 'av roller på engelska' },
    remote:    { en: 'remote or hybrid',    fi: 'etä- tai hybridityötä', sv: 'distans eller hybrid' },
    fulltime:  { en: 'full-time',           fi: 'kokoaikatyötä',         sv: 'heltid' },
    skills:    { en: 'popular skills', fi: 'suositut taidot',  sv: 'populära kompetenser' },
    benefits:  { en: 'common benefits', fi: 'yleisimmät edut', sv: 'vanliga förmåner' },
  }
  return labels[key]?.[lang] || key
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function sortByPriority(filters: ActiveFilter[]): ActiveFilter[] {
  return [...filters].sort(
    (a, b) => FILTER_PRIORITY.indexOf(a.type) - FILTER_PRIORITY.indexOf(b.type),
  )
}

/**
 * Formats a job count for embedding in a title/description. Finnish and Swedish both group
 * thousands with a plain space (`1 240`), not the `en-US` comma this used to render even inside
 * fi/sv copy. Space-grouping via `toLocaleString('fi-FI'/'sv-SE')` also risks a narrow no-break
 * space depending on the runtime's ICU data, so this normalises to a literal ASCII space instead
 * for consistent output everywhere this renders (SERP titles, HTML, crawlers).
 */
export function fmtCount(n: number, lang: SRPLanguage = 'en'): string {
  const withCommas = n.toLocaleString('en-US')
  return lang === 'en' ? withCommas : withCommas.replace(/,/g, ' ')
}

/**
 * en keeps the compact "4k" / "4,200" forms. fi/sv always spell the number out with a plain
 * space as the thousands separator ("4 200"): "4k €/kk" is not something a Finnish job seeker
 * would ever read in a job ad, and the en-US comma reads as a decimal mark in both languages.
 */
export function fmtSalary(n: number, lang: SRPLanguage = 'en'): string {
  if (lang !== 'en') return n.toLocaleString('en-US').replace(/,/g, ' ')
  if (n >= 1000 && n % 1000 === 0) return `${n / 1000}k`
  return n.toLocaleString('en-US')
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Truncate title to the shared SEO title cap (see `DEFAULT_SEO_TITLE_MAX_LENGTH`), preserving
 *  the " | JobCrawls" suffix. Delegates to the same implementation every other page type's
 *  title passes through downstream, so a generated SRP title is truncated exactly once. */
function trunc(title: string): string {
  return truncateSeoTitle(title, DEFAULT_SEO_TITLE_MAX_LENGTH)
}

/** Truncate description at sentence boundary, or word boundary as fallback.
 *  Never truncates below MIN_DESCRIPTION_LENGTH to preserve the length guarantee. */
function truncDesc(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text

  const cut = text.substring(0, maxLen)
  const lastPeriod = cut.lastIndexOf('.')
  if (lastPeriod >= MIN_DESCRIPTION_LENGTH) {
    return cut.substring(0, lastPeriod + 1)
  }

  const lastSpace = cut.lastIndexOf(' ')
  if (lastSpace > MIN_DESCRIPTION_LENGTH) {
    return cut.substring(0, lastSpace) + '...'
  }

  return cut + '...'
}
